import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, runWithAuditReason } from '../../api/client';
import { useAuthStore } from '../../auth/store';
import { FormTitle } from '../../components/FormTitle';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { DualListPickerDialog } from '../../components/DualListPicker';
import { ComboSelect } from '../../components/ComboSelect';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../components/confirm';
import { ViewToggle } from '../../components/ViewToggle';
import { CellContextMenu, canBulkCopy } from '../../components/gridEdit';
import { EmployeeDetailForm, type EmployeeTab } from './EmployeeDetailForm';
import { RichTextEditor } from '../settings/systemManagement/RichTextEditor';
import './usersDetail.css';

// ─────────────────────────────────────────────────────────────────────────
// User & Security → Users  (ALPACA List→Detail anatomy, BO UI Standard skin)
// ─────────────────────────────────────────────────────────────────────────
//   Edits the LIVE XAF identity pair PermissionPolicyUser + Employee (shared
//   with ALPACA).  LIST (landing view, shared <AppDataGrid>) → row-click /
//   Edit opens the FULL-PAGE six-tab Employee detail (EmployeeDetailForm —
//   ALPACA Employee_DetailView tabs), replacing the list in-page.
//   All data logic (fetch / save / password / activate / delete / role
//   link-unlink / copy-roles) is the pre-existing wiring — layout only.
//
//   ALPACA field parity (2026-07-02): the LIST carries the "Lab User" column
//   (ALPACA order: Full Name · User Name · Lab User · Role · Department · Is
//   Active · Is Admin) and the detail carries Employment/Retire Date
//   (Organization), the full Contact set (Office/Home/Other Phone, Fax,
//   Country → State → City cascading lookups, Zip, Address) and "Change
//   Password On First Logon" (User Information) — all backed by the users
//   GET/PUT (UserAdminDetail / UserAdminUpsert).
//
//   Still OMITTED (needs its own spec — do NOT invent endpoints here):
//     · Toolbar "Default E-mail Recipient" — the legacy action edits the
//       dbo.DefaultMailRecipient singleton, which AI already owns via
//       Settings → Report Delivery Settings (standing rule 8: reuse, never
//       duplicate a second write path).
// ─────────────────────────────────────────────────────────────────────────

function lcKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(lcKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k.toLowerCase()] = lcKeys(val);
    return out;
  }
  return v;
}
const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
  ?? (e as { message?: string }).message ?? 'Request failed.';

// lcKeys lowercases every key (so it works whether the API serialises camelCase
// or PascalCase); these mappers turn that lowercase bag back into our typed,
// camelCase shapes.
type Rec = Record<string, unknown>;
const arr = (data: unknown): Rec[] => (((lcKeys(data) as { data?: Rec[] }).data) ?? []);
const one = (data: unknown): Rec | undefined => ((lcKeys(data) as { data?: Rec }).data);
const mapUser = (r: Rec): ListItem => ({
  oid: String(r.oid), userName: String(r.username ?? ''), displayName: String(r.displayname ?? ''),
  fullName: (r.fullname as string) ?? null, isLabUser: !!r.islabuser, role: (r.role as string) ?? null,
  department: (r.department as string) ?? null, isLimsAdmin: !!r.islimsadmin, isActive: !!r.isactive,
});
const mapDetail = (r: Rec): Detail => ({
  oid: String(r.oid), userName: String(r.username ?? ''), firstName: (r.firstname as string) ?? null,
  lastName: (r.lastname as string) ?? null, displayName: (r.displayname as string) ?? null,
  email: (r.email as string) ?? null, tFAEmail: (r.tfaemail as string) ?? null,
  mobilePhone: (r.mobilephone as string) ?? null, positionId: (r.positionid as string) ?? null,
  departmentId: (r.departmentid as string) ?? null, companyId: (r.companyid as string) ?? null,
  isLimsAdmin: !!r.islimsadmin, isActive: !!r.isactive, userId: Number(r.userid ?? 0), hasPassword: !!r.haspassword,
  initial: (r.initial as string) ?? null, jobTitle: (r.jobtitle as string) ?? null,
  isLabUser: !!r.islabuser, language: Number(r.language ?? 1), isEnabledLanguage: !!r.isenabledlanguage,
  gender: r.gender == null ? null : Number(r.gender), birthday: (r.birthday as string) ?? null,
  resume: (r.resume as string) ?? null, photo: (r.photo as string) ?? null, signature: (r.signature as string) ?? null,
  // ALPACA field parity (2026-07-02)
  employmentDate: (r.employmentdate as string) ?? null, retireDate: (r.retiredate as string) ?? null,
  officePhone: (r.officephone as string) ?? null, homePhone: (r.homephone as string) ?? null,
  otherPhone: (r.otherphone as string) ?? null, fax: (r.fax as string) ?? null,
  countryId: (r.countryid as string) ?? null, stateId: (r.stateid as string) ?? null, cityId: (r.cityid as string) ?? null,
  zip: (r.zip as string) ?? null, address: (r.address as string) ?? null,
  changePasswordOnFirstLogon: !!r.changepasswordonfirstlogon,
});
const mapRole = (r: Rec): Role => ({ id: String(r.id), name: String(r.name ?? ''), isAdministrative: !!r.isadministrative });
const mapLookup = (r: Rec): Lookup => ({ id: String(r.id), name: String(r.name ?? '') });
const mapCascade = (r: Rec): Cascade => ({ id: String(r.id), name: String(r.name ?? ''), parentId: (r.parentid as string) ?? null });

// Photo/Signature come back as raw base64 (System.Text.Json encodes the byte[]); build a
// display data URL, sniffing JPEG/GIF/PNG from the leading base64 chars. stripDataUrl is the
// inverse for the save payload (server strips a data: prefix too, belt-and-braces).
const imgDataUrl = (b64: string | null | undefined): string | null => {
  if (!b64) return null;
  const mime = b64.startsWith('/9j/') ? 'image/jpeg' : b64.startsWith('R0lGOD') ? 'image/gif' : 'image/png';
  return `data:${mime};base64,${b64}`;
};
const stripDataUrl = (v: string | null): string => {
  if (!v) return '';
  const i = v.indexOf(',');
  return v.startsWith('data:') && i >= 0 ? v.slice(i + 1) : v;
};

type ListItem = { oid: string; userName: string; displayName: string; fullName: string | null; isLabUser: boolean; role: string | null; department: string | null; isLimsAdmin: boolean; isActive: boolean };
type Lookup = { id: string; name: string };
type Cascade = { id: string; name: string; parentId: string | null };
type Role = { id: string; name: string; isAdministrative: boolean };
type Detail = {
  oid: string; userName: string; firstName: string | null; lastName: string | null; displayName: string | null;
  email: string | null; tFAEmail: string | null; mobilePhone: string | null;
  positionId: string | null; departmentId: string | null; companyId: string | null;
  isLimsAdmin: boolean; isActive: boolean; userId: number; hasPassword: boolean;
  initial: string | null; jobTitle: string | null; isLabUser: boolean; language: number; isEnabledLanguage: boolean;
  gender: number | null; birthday: string | null; resume: string | null; photo: string | null; signature: string | null;
  // ALPACA field parity (2026-07-02)
  employmentDate: string | null; retireDate: string | null;
  officePhone: string | null; homePhone: string | null; otherPhone: string | null; fax: string | null;
  countryId: string | null; stateId: string | null; cityId: string | null;
  zip: string | null; address: string | null;
  changePasswordOnFirstLogon: boolean;
};
type Form = {
  userName: string; firstName: string; lastName: string; displayName: string;
  email: string; tFAEmail: string; mobilePhone: string;
  positionId: string; departmentId: string; companyId: string; isLimsAdmin: boolean;
  initial: string; jobTitle: string; isLabUser: boolean; language: number; isEnabledLanguage: boolean;
  // HR fields. photo/signature hold a display data URL (server image on load, or a freshly
  // picked one); the *Changed flag tells the save whether to re-write that image column.
  gender: number | null; birthday: string; resume: string;
  photo: string | null; photoChanged: boolean; signature: string | null; signatureChanged: boolean;
  // ALPACA field parity (2026-07-02) — dates as yyyy-MM-dd input strings.
  employmentDate: string; retireDate: string;
  officePhone: string; homePhone: string; otherPhone: string; fax: string;
  countryId: string; stateId: string; cityId: string;
  zip: string; address: string;
  changePasswordOnFirstLogon: boolean;
};
const BLANK: Form = { userName: '', firstName: '', lastName: '', displayName: '', email: '', tFAEmail: '', mobilePhone: '', positionId: '', departmentId: '', companyId: '', isLimsAdmin: false, initial: '', jobTitle: '', isLabUser: false, language: 1, isEnabledLanguage: false, gender: null, birthday: '', resume: '', photo: null, photoChanged: false, signature: null, signatureChanged: false, employmentDate: '', retireDate: '', officePhone: '', homePhone: '', otherPhone: '', fax: '', countryId: '', stateId: '', cityId: '', zip: '', address: '', changePasswordOnFirstLogon: false };

const TAB_IDS = ['basic', 'org', 'contact', 'resume', 'user', 'roles'] as const;
type TabId = typeof TAB_IDS[number];

// ALPACA parity (2026-07-20, user decision): legacy hides this tab on the
// SAME Employee_DetailView (Model.xafml Item6, Removed="True" — confirmed by
// the blank unlabelled 6th tab slot legacy actually renders). UI-only hide —
// the underlying roles/link-unlink data and plumbing (userRoles, roleSel,
// linkOpen, etc.) is untouched, so re-enabling is a one-line flip. KNOWN
// TRADE-OFF: unlike legacy (which assigns a role via a single-value "Role"
// dropdown elsewhere on this same view — not yet built in AI), this WAS the
// only UI in AI for assigning a role to a specific user; with it hidden,
// there is currently no other screen that does this. Accepted knowingly,
// per explicit user request, over the alternative of a fake/unbacked field.
const SHOW_ROLES_TAB = false;

// ALPACA export column set for the Users list (order matches the legacy CSV;
// NOT the grid columns — the grid has an edit-glyph column that never exports).
const USER_EXPORT_COLS: { header: string; value: (u: ListItem) => string }[] = [
  { header: 'Full Name', value: u => u.fullName ?? '' },
  { header: 'User Name', value: u => u.userName },
  { header: 'Lab User', value: u => u.isLabUser ? 'Yes' : 'No' },
  { header: 'Role', value: u => u.role ?? '' },
  { header: 'Department', value: u => u.department ?? '' },
  { header: 'Is Active', value: u => u.isActive ? 'Yes' : 'No' },
  { header: 'Is Admin', value: u => u.isLimsAdmin ? 'Yes' : 'No' },
];

export default function UsersAdminPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  // ── data / list state (existing wiring) ────────────────────────────────
  const [list, setList]         = useState<ListItem[]>([]);
  const [lookups, setLookups]   = useState<{ positions: Lookup[]; departments: Lookup[]; companies: Lookup[]; countries: Cascade[]; states: Cascade[]; cities: Cascade[] }>({ positions: [], departments: [], companies: [], countries: [], states: [], cities: [] });
  const [loading, setLoading]   = useState(true);
  const [loadMs, setLoadMs]     = useState<number | null>(null);
  const [err, setErr]           = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // ── detail state (existing wiring, full-page view) ──────────────────────
  // Opening an EXISTING record lands in read-only 'view' mode first (ALPACA
  // parity, BO UI Standard v2 2026-07-02); the detail's Edit button switches
  // to 'edit'.  New goes straight to 'new' (edit-style bar).
  const [view, setView]         = useState<'list' | 'detail'>('list');
  const [mode, setMode]         = useState<'new' | 'view' | 'edit'>('new');
  const [selOid, setSelOid]     = useState<string | null>(null);
  const [form, setForm]         = useState<Form>(BLANK);
  const [newPwd, setNewPwd]     = useState('');
  const [detail, setDetail]     = useState<Detail | null>(null);
  const [saving, setSaving]     = useState(false);
  const [dtab, setDtab]         = useState<TabId>('basic');

  // ── dialogs ──────────────────────────────────────────────────────────────
  // isNew: legacy's Set Password dialog (Model.xafml:22030-22031) works the
  // SAME on an unsaved record — no Oid to PUT to yet, so isNew just sets the
  // local newPwd (sent with the Create payload on Save) instead of calling
  // the password-reset endpoint immediately.
  const [pwdTarget, setPwdTarget] = useState<{ oid: string; userName: string; isNew?: boolean } | null>(null);
  const [pwdValue, setPwdValue]   = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [copyTarget, setCopyTarget] = useState<{ oid: string; name: string } | null>(null);
  const [copyFrom, setCopyFrom]     = useState('');

  // ── roles (existing wiring) ─────────────────────────────────────────────
  const [allRoles, setAllRoles]       = useState<Role[]>([]);
  const [userRoles, setUserRoles]     = useState<string[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);
  const [roleSel, setRoleSel]         = useState<Set<string | number>>(new Set());
  const [linkOpen, setLinkOpen]       = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      // ALPACA parity (2026-07-20): legacy's Employee_ListView shows BOTH active and
      // inactive users together, with the "Is Active" column just indicating status —
      // it never hides inactive rows. So the fetch always includes everyone; showInactive
      // (below) only narrows the CLIENT-side view to inactive-only when explicitly ticked.
      api.get('/api/v1/security/users?includeInactive=true'),
      lookups.positions.length === 0 ? api.get('/api/v1/security/users/lookups') : Promise.resolve(null),
      allRoles.length === 0 ? api.get('/api/v1/security/users/roles') : Promise.resolve(null),
    ]).then(([lr, kr, rr]) => {
      setList(arr(lr.data).map(mapUser));
      if (kr) {
        const d = one(kr.data);
        if (d) setLookups({
          positions: ((d.positions as Rec[]) ?? []).map(mapLookup),
          departments: ((d.departments as Rec[]) ?? []).map(mapLookup),
          companies: ((d.companies as Rec[]) ?? []).map(mapLookup),
          countries: ((d.countries as Rec[]) ?? []).map(mapCascade),
          states: ((d.states as Rec[]) ?? []).map(mapCascade),
          cities: ((d.cities as Rec[]) ?? []).map(mapCascade),
        });
      }
      if (rr) setAllRoles(arr(rr.data).map(mapRole));
    }).catch(e => setErr(errMsg(e))).finally(() => { setLoadMs(performance.now() - t0); setLoading(false); });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps — fetch is static now (always includeInactive=true)

  const shown = useMemo(() => {
    const t = search.trim().toLowerCase();
    // ALPACA parity: default shows EVERYONE (active + inactive) — "Show inactive
    // users only" is an AI-added filter to isolate inactive rows, not a requirement
    // to see them at all (legacy never hides them).
    const base = showInactive ? list.filter(u => !u.isActive) : list;
    return t ? base.filter(u => u.userName.toLowerCase().includes(t) || u.displayName.toLowerCase().includes(t) || (u.fullName ?? '').toLowerCase().includes(t)) : base;
  }, [list, search, showInactive]);

  const selectedRow = useMemo(
    () => (selected.size === 1 ? list.find(u => u.oid === Array.from(selected)[0]) ?? null : null),
    [selected, list]);

  // ── open detail (existing fetch wiring) ─────────────────────────────────
  const openNew = () => {
    setMode('new'); setSelOid(null); setDetail(null); setForm(BLANK); setNewPwd('');
    setUserRoles([]); setRoleSel(new Set()); setDtab('basic');
    setView('detail');
    // ALPACA parity: Employee's constructor defaults a NEW record's Company
    // ("Division") to the CURRENTLY LOGGED-IN admin's own Company
    // (Employee.cs:69 — `Company = ...CurrentUser.Company`). Best-effort —
    // if this lookup fails, the admin still fills it in manually (it's
    // required, so save() blocks an empty submit either way).
    const myOid = useAuthStore.getState().user?.id;
    if (myOid) {
      api.get(`/api/v1/security/users/${myOid}`).then(r => {
        const d = one(r.data);
        const companyId = d?.companyid as string | undefined;
        if (companyId) setForm(p => ({ ...p, companyId }));
      }).catch(() => { /* default is a convenience, not required */ });
    }
  };

  // My Details' "New" button (ALPACA Employee_DetailView parity — New is
  // shared across every entry point to this BO, not just the Users list)
  // deep-links here with ?new=1 and expects the blank create form to open
  // immediately. Strip the param right after so a later refresh doesn't
  // re-trigger it.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      openNew();
      navigate('/m/sec-users', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = async (oid: string) => {
    setSelOid(oid); setDetail(null); setRoleSel(new Set());
    try {
      const [r, rolesRes] = await Promise.all([
        api.get(`/api/v1/security/users/${oid}`),
        api.get(`/api/v1/security/users/${oid}/roles`),
      ]);
      const raw = one(r.data);
      setUserRoles(((lcKeys(rolesRes.data) as { data?: string[] }).data) ?? []);
      if (raw) {
        const d = mapDetail(raw);
        setDetail(d);
        setForm({
          userName: d.userName ?? '', firstName: d.firstName ?? '', lastName: d.lastName ?? '', displayName: d.displayName ?? '',
          email: d.email ?? '', tFAEmail: d.tFAEmail ?? '', mobilePhone: d.mobilePhone ?? '',
          positionId: d.positionId ?? '', departmentId: d.departmentId ?? '', companyId: d.companyId ?? '', isLimsAdmin: d.isLimsAdmin,
          initial: d.initial ?? '', jobTitle: d.jobTitle ?? '', isLabUser: d.isLabUser, language: d.language ?? 1, isEnabledLanguage: d.isEnabledLanguage,
          gender: d.gender, birthday: d.birthday ? String(d.birthday).slice(0, 10) : '', resume: d.resume ?? '',
          photo: imgDataUrl(d.photo), photoChanged: false, signature: imgDataUrl(d.signature), signatureChanged: false,
          employmentDate: d.employmentDate ? String(d.employmentDate).slice(0, 10) : '',
          retireDate: d.retireDate ? String(d.retireDate).slice(0, 10) : '',
          officePhone: d.officePhone ?? '', homePhone: d.homePhone ?? '', otherPhone: d.otherPhone ?? '', fax: d.fax ?? '',
          countryId: d.countryId ?? '', stateId: d.stateId ?? '', cityId: d.cityId ?? '',
          zip: d.zip ?? '', address: d.address ?? '',
          changePasswordOnFirstLogon: d.changePasswordOnFirstLogon,
        });
        // Opening from the list (row click / pencil / prev-next / Detail tab)
        // always lands in read-only VIEW mode; Edit unlocks it.
        setMode('view');
        setView('detail');
      }
    } catch (e) { notify.error(errMsg(e)); }
  };

  const backToList = () => { setView('list'); setSelOid(null); setDetail(null); setDtab('basic'); };

  // Detail-view toolbar parity (matches the Module Access Control / Roles
  // fix): New / Delete / Refresh alongside Edit — not just Edit + Close.
  const refreshCurrent = () => { load(); if (selOid) void openEdit(selOid); };
  const deleteCurrent = async () => {
    if (!selOid) return;
    const row = list.find(u => u.oid === selOid);
    const label = row?.userName ?? form.userName;
    if (!(await confirm.confirm({ title: 'Delete user?', message: `Delete "${label}"? (soft-delete — recoverable)` }))) return;
    try {
      await api.delete(`/api/v1/security/users/${selOid}`);
      notify.success('User deleted.');
      setSelected(new Set());
      backToList();
      load();
    } catch (e) { notify.error(errMsg(e)); }
  };

  const up = <K extends keyof Form>(k: K, v: Form[K]) => setForm(p => ({ ...p, [k]: v }));
  // Read a picked image file into a display data URL (capped at 4 MB — these live in the
  // Employee row, not a blob store).
  const readImgFile = (e: React.ChangeEvent<HTMLInputElement>, set: (v: string) => void) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { notify.error('Image must be 4 MB or smaller.'); return; }
    const r = new FileReader();
    r.onload = () => set(String(r.result));
    r.readAsDataURL(file);
  };
  const body = () => ({
    userName: form.userName.trim(), firstName: form.firstName.trim() || null, lastName: form.lastName.trim() || null,
    displayName: form.displayName.trim() || null, email: form.email.trim() || null, tFAEmail: form.tFAEmail.trim() || null,
    mobilePhone: form.mobilePhone.trim() || null,
    positionId: form.positionId || null, departmentId: form.departmentId || null, companyId: form.companyId || null,
    isLimsAdmin: form.isLimsAdmin,
    initial: form.initial.trim() || null, jobTitle: form.jobTitle.trim() || null,
    isLabUser: form.isLabUser, language: form.language, isEnabledLanguage: form.isEnabledLanguage,
    gender: form.gender, birthday: form.birthday || null, resume: form.resume.trim() || null,
    // null = don't touch; '' = clear; base64 = set (photoChanged/signatureChanged guard it).
    photoBase64: form.photoChanged ? stripDataUrl(form.photo) : null,
    signatureBase64: form.signatureChanged ? stripDataUrl(form.signature) : null,
    // ALPACA field parity (2026-07-02)
    employmentDate: form.employmentDate || null, retireDate: form.retireDate || null,
    officePhone: form.officePhone.trim() || null, homePhone: form.homePhone.trim() || null,
    otherPhone: form.otherPhone.trim() || null, fax: form.fax.trim() || null,
    countryId: form.countryId || null, stateId: form.stateId || null, cityId: form.cityId || null,
    zip: form.zip.trim() || null, address: form.address.trim() || null,
    changePasswordOnFirstLogon: form.changePasswordOnFirstLogon,
  });

  // ── save (existing POST/PUT wiring) ─────────────────────────────────────
  // ALPACA parity (2026-07-21, XAF Save trio): Save stays open on the saved
  // record (read-only VIEW, matching SettingsDetailPage's own 'stay'
  // convention) · Save and Close returns to the list · Save and New resets
  // to a blank record for continued entry — same shared save/confirm/audit
  // flow (useSaveFlow) for all three, only the post-save navigation differs.
  const save = (after: 'stay' | 'close' | 'new' = 'stay') => {
    if (!form.userName.trim()) { notify.error('Email is required.'); setDtab('user'); return; }
    if (mode === 'new' && !newPwd) { notify.error('A password is required for a new user.'); setDtab('user'); return; }
    // ALPACA parity: Employee.DisplayName — RuleRequiredField (Employee.cs:1134).
    if (!form.displayName.trim()) { notify.error('Display Name is required.'); setDtab('basic'); return; }
    // ALPACA parity: Employee.Company ("Division") — RuleRequiredField (Employee.cs:118).
    if (!form.companyId) { notify.error('Division is required.'); setDtab('org'); return; }
    // ALPACA parity: Employee.Email ("Delivery Email") — RuleRequiredField, Model.xafml:2383.
    if (!form.email.trim()) { notify.error('Delivery Email is required.'); setDtab('contact'); return; }
    if (form.employmentDate && form.retireDate && form.retireDate < form.employmentDate) {
      notify.error('Retire Date cannot be earlier than Employment Date.'); setDtab('org'); return;
    }
    setSaving(true);
    const wasNew = mode === 'new';
    let createdOid: string | null = null;
    runSave({
      save: async () => {
        if (wasNew) {
          const res = await api.post('/api/v1/security/users', { ...body(), password: newPwd });
          createdOid = (one(res.data)?.oid as string | undefined) ?? null;
        } else if (selOid) {
          await api.put(`/api/v1/security/users/${selOid}`, body());
        }
      },
      successMessage: wasNew ? 'User created successfully.' : 'User saved successfully.',
      onSaved: () => {
        load();
        if (after === 'close') { backToList(); return; }
        if (after === 'new') { openNew(); return; }
        // 'stay' — remain on the saved record, back in read-only VIEW mode.
        if (wasNew) { if (createdOid) void openEdit(createdOid); else backToList(); }
        else if (selOid) void openEdit(selOid);
      },
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSaving(false));
  };

  // ── Set Password (existing wiring, AppDialog — extended 2026-07-20 for ALPACA
  //    parity: legacy's dialog always has New + Confirm Password, on BOTH new and
  //    existing records) ───────────────────────────────────────────────────
  const setPassword = () => {
    if (!pwdTarget || !pwdValue) { notify.error('Enter a new password.'); return; }
    if (pwdValue !== pwdConfirm) { notify.error('Passwords do not match.'); return; }
    if (pwdTarget.isNew) {
      // Unsaved record — nothing to PUT to yet; just fill the Initial Password
      // field that Save's Create payload already sends.
      setNewPwd(pwdValue);
      notify.success('Password set — will be saved with the record.');
      setPwdTarget(null); setPwdValue(''); setPwdConfirm('');
      return;
    }
    runSave({
      save: async () => { await api.post(`/api/v1/security/users/${pwdTarget.oid}/password`, { password: pwdValue }); setPwdTarget(null); setPwdValue(''); setPwdConfirm(''); },
      successMessage: 'Password reset successfully.',
      onError: e => notify.error(errMsg(e)),
    });
  };

  // ── Activate / Deactivate (existing wiring, User Information tab) ───────
  const toggleActive = async () => {
    if (!detail) return;
    try {
      await api.post(`/api/v1/security/users/${detail.oid}/active`, { active: !detail.isActive });
      notify.success(detail.isActive ? 'User deactivated.' : 'User activated.');
      load(); await openEdit(detail.oid);
    } catch (e) { notify.error(errMsg(e)); }
  };

  // ── Delete (existing wiring, list toolbar) ──────────────────────────────
  const del = async (target: ListItem) => {
    if (!(await confirm.confirm({ title: 'Delete user?', message: `Delete "${target.userName}"? (soft-delete — recoverable)` }))) return;
    try {
      await api.delete(`/api/v1/security/users/${target.oid}`);
      notify.success('User deleted.');
      setSelected(new Set());
      if (selOid === target.oid) backToList();
      load();
    } catch (e) { notify.error(errMsg(e)); }
  };

  // ── roles: Link / Unlink both persist via the EXISTING PUT roles call ───
  const hasRole = (id: string) => userRoles.some(x => x.toLowerCase() === id.toLowerCase());
  const assignedRoles = useMemo(() => allRoles.filter(r => hasRole(r.id)), [allRoles, userRoles]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyRoles = (roleIds: string[], msg: string) => {
    if (!selOid) return;
    setSavingRoles(true);
    runSave({
      save: async () => {
        const r = await api.put(`/api/v1/security/users/${selOid}/roles`, { roleIds });
        setUserRoles(((lcKeys(r.data) as { data?: string[] }).data) ?? roleIds);
        setRoleSel(new Set());
      },
      successMessage: msg,
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSavingRoles(false));
  };

  const unlinkSelected = () => {
    if (roleSel.size === 0) { notify.warning('Select at least one role to unlink.'); return; }
    const drop = new Set(Array.from(roleSel).map(x => String(x).toLowerCase()));
    applyRoles(userRoles.filter(id => !drop.has(id.toLowerCase())), 'Roles updated successfully.');
  };

  // ── Copy Roles (existing POST copy-roles wiring, AppDialog) ─────────────
  const copyRoles = async () => {
    if (!copyTarget || !copyFrom) { notify.error('Pick a user to copy roles from.'); return; }
    try {
      const r = await api.post(`/api/v1/security/users/${copyTarget.oid}/copy-roles`, { fromOid: copyFrom });
      // If the target is the record currently open in the detail, refresh its roles.
      if (selOid === copyTarget.oid) setUserRoles(((lcKeys(r.data) as { data?: string[] }).data) ?? []);
      setCopyTarget(null); setCopyFrom('');
      notify.success('Roles copied successfully.');
    } catch (e) { notify.error(errMsg(e)); }
  };

  // ── UI-8: inline (in-cell) editing on the LIST grid (ALPACA Employee_ListView
  //    parity).  Double-click User Name / Role / Department → editor appears →
  //    Enter/blur (or dropdown pick) commits via the EXISTING PUTs → useNotify;
  //    Escape reverts.  Single click keeps opening the detail (deferred 250 ms so
  //    a double-click can cancel it).  Booleans stay cellcheck (Is Active changes
  //    via the detail's Activate/Deactivate).  Reason gate = standing rule 11.
  //    §9 bulk copy (right-click Copy to All/Selected, shared CellContextMenu) is
  //    WIRED on Department + Role (2026-07-02): it calls the bulk endpoint
  //    PUT /api/v1/security/users/bulk-field, which applies the value to every
  //    target in ONE request and writes ONE audit row for the whole copy (the
  //    standard's one-audit-row-per-bulk-copy semantics).
  type InlineKey = 'userName' | 'role' | 'department';
  const gateReason = useReasonGate();
  const [editCell, setEditCell] = useState<{ oid: string; key: InlineKey } | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const clickTimer = useRef<number | null>(null);

  const deptIdOf = (row: ListItem) => lookups.departments.find(d => d.name === row.department)?.id ?? '';
  const roleIdOf = (row: ListItem) => allRoles.find(r => r.name.toLowerCase() === (row.role ?? '').toLowerCase())?.id ?? '';

  const startInlineEdit = (row: ListItem, key: InlineKey) => {
    setEditDraft(key === 'userName' ? row.userName : key === 'department' ? deptIdOf(row) : roleIdOf(row));
    setEditCell({ oid: row.oid, key });
  };
  const cancelInlineEdit = () => setEditCell(null);

  const commitInlineEdit = async (override?: string) => {
    if (!editCell || savingCell) return;
    const { oid, key } = editCell;
    const row = list.find(u => u.oid === oid);
    if (!row) { setEditCell(null); return; }
    const draft = (override ?? editDraft).trim();
    if (key === 'userName' && !draft) { notify.warning('User Name is required.'); return; }
    const unchanged =
      (key === 'userName' && draft === row.userName) ||
      (key === 'department' && draft === deptIdOf(row)) ||
      (key === 'role' && draft === roleIdOf(row));
    if (unchanged) { setEditCell(null); return; }
    // Reason-for-change gate (standing rule 11) — inline edit is an Update.
    const reason = await gateReason('Users', 'edit');
    if (reason === null) { setEditCell(null); return; }
    setSavingCell(true);
    try {
      await runWithAuditReason(reason, async () => {
        if (key === 'role') {
          // Swap the DISPLAYED role for the picked one, keeping the user's other
          // roles — persists via the EXISTING PUT /users/{oid}/roles (audited).
          const rr = await api.get(`/api/v1/security/users/${oid}/roles`);
          const cur = ((lcKeys(rr.data) as { data?: string[] }).data) ?? [];
          const prevId = roleIdOf(row).toLowerCase();
          const next = cur.filter(id => id.toLowerCase() !== prevId);
          if (draft && !next.some(id => id.toLowerCase() === draft.toLowerCase())) next.push(draft);
          await api.put(`/api/v1/security/users/${oid}/roles`, { roleIds: next });
        } else {
          // Pull the full current record, override the one edited field, PUT it
          // back through the EXISTING audited user-update handler.
          const raw = one((await api.get(`/api/v1/security/users/${oid}`)).data);
          if (!raw) throw new Error('User not found.');
          const d = mapDetail(raw);
          await api.put(`/api/v1/security/users/${oid}`, {
            userName: key === 'userName' ? draft : d.userName,
            firstName: d.firstName, lastName: d.lastName, displayName: d.displayName,
            email: d.email, tFAEmail: d.tFAEmail, mobilePhone: d.mobilePhone,
            positionId: d.positionId,
            departmentId: key === 'department' ? (draft || null) : d.departmentId,
            companyId: d.companyId, isLimsAdmin: d.isLimsAdmin,
            initial: d.initial, jobTitle: d.jobTitle,
            isLabUser: d.isLabUser, language: d.language, isEnabledLanguage: d.isEnabledLanguage,
            gender: d.gender, birthday: d.birthday ? String(d.birthday).slice(0, 10) : null,
            resume: d.resume,
            photoBase64: null, signatureBase64: null, // null = don't touch (server contract)
            // Round-trip the parity fields unchanged (the PUT rewrites every column).
            employmentDate: d.employmentDate ? String(d.employmentDate).slice(0, 10) : null,
            retireDate: d.retireDate ? String(d.retireDate).slice(0, 10) : null,
            officePhone: d.officePhone, homePhone: d.homePhone, otherPhone: d.otherPhone, fax: d.fax,
            countryId: d.countryId, stateId: d.stateId, cityId: d.cityId,
            zip: d.zip, address: d.address,
            changePasswordOnFirstLogon: d.changePasswordOnFirstLogon,
          });
        }
      });
      setEditCell(null);
      notify.success('Change saved.');
      load();
    } catch (e) { notify.error(errMsg(e)); }
    finally { setSavingCell(false); }
  };

  // ── §9 bulk copy (2026-07-02) — right-click a filled Department / Role cell →
  //    shared CellContextMenu → Copy to All Cells (current filtered list) or
  //    Copy to Selected Rows (checkbox selection) → the reason gate → ONE call
  //    to PUT /api/v1/security/users/bulk-field (ONE audit row server-side).
  type BulkKey = 'department' | 'role';
  const [bulkCtx, setBulkCtx] = useState<{ x: number; y: number; key: BulkKey; row: ListItem } | null>(null);

  const openBulkCtx = (e: React.MouseEvent, row: ListItem, key: InlineKey) => {
    if (key === 'userName') return;                            // unique per user — never bulk-copied
    e.preventDefault(); e.stopPropagation();
    const display = key === 'role' ? row.role : row.department;
    if (!canBulkCopy(display)) { notify.warning('Cell is empty.'); return; }
    const id = key === 'role' ? roleIdOf(row) : deptIdOf(row);
    if (!id) { notify.warning(key === 'role' ? 'Only a single-role cell can be bulk-copied.' : 'Unknown department.'); return; }
    setBulkCtx({ x: e.clientX, y: e.clientY, key, row });
  };

  const bulkApply = async (scope: 'all' | 'selected') => {
    if (!bulkCtx) return;
    const { key, row } = bulkCtx;
    const value = key === 'role' ? roleIdOf(row) : deptIdOf(row);
    const oids = scope === 'all' ? shown.map(u => u.oid) : Array.from(selected).map(String);
    if (oids.length === 0) { notify.warning('Select rows first.'); return; }
    // Reason-for-change gate (standing rule 11) — a bulk copy is an Update.
    const reason = await gateReason('Users', 'edit');
    if (reason === null) return;
    try {
      await runWithAuditReason(reason, () =>
        api.put('/api/v1/security/users/bulk-field', { field: key, value, oids }));
      notify.success(`${key === 'role' ? 'Role' : 'Department'} copied to ${oids.length} user${oids.length === 1 ? '' : 's'}.`);
      load();
    } catch (e) { notify.error(errMsg(e)); }
  };

  const inlineCell = (row: ListItem, key: InlineKey, display: string) => {
    const editing = editCell?.oid === row.oid && editCell.key === key;
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    if (editing) {
      if (key === 'userName') {
        return (
          <span onClick={stop} onDoubleClick={stop}>
            <input autoFocus className="ai-inline-edit" value={editDraft} disabled={savingCell}
              onChange={e => setEditDraft(e.target.value)}
              onBlur={() => { void commitInlineEdit(); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); void commitInlineEdit(); }
                else if (e.key === 'Escape') { e.preventDefault(); cancelInlineEdit(); }
              }} />
          </span>
        );
      }
      const opts = key === 'role'
        ? allRoles.map(r => ({ value: r.id, label: r.name }))
        : lookups.departments.map(d => ({ value: d.id, label: d.name }));
      return (
        <span onClick={stop} onDoubleClick={stop}>
          <select autoFocus className="ai-inline-edit" value={editDraft} disabled={savingCell}
            onChange={e => { setEditDraft(e.target.value); void commitInlineEdit(e.target.value); }}
            onBlur={() => cancelInlineEdit()}
            onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); cancelInlineEdit(); } }}>
            <option value="">—</option>
            {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </span>
      );
    }
    return (
      <span className="ai-editcell" title="Double-click to edit"
        onClick={e => {
          // Defer the row's open-detail so a double-click can cancel it.
          e.stopPropagation();
          if (clickTimer.current) window.clearTimeout(clickTimer.current);
          clickTimer.current = window.setTimeout(() => { clickTimer.current = null; void openEdit(row.oid); }, 250);
        }}
        onDoubleClick={e => {
          e.stopPropagation();
          if (clickTimer.current) { window.clearTimeout(clickTimer.current); clickTimer.current = null; }
          startInlineEdit(row, key);
        }}
        onContextMenu={e => openBulkCtx(e, row, key)}>
        {display || ' '}
      </span>
    );
  };

  // ── list grid (ALPACA Employee_ListView column order; booleans = cellcheck) ──
  const cols: AppColumn<ListItem>[] = [
    // ALPACA-style per-row edit glyph — first data column, right after the
    // selection checkbox (AppDataGrid renders `selectable` checkboxes itself).
    { field: '__edit', header: 'Edit', width: 44, align: 'center',
      sortable: false, filterable: false,
      render: r => (
        <button type="button" className="sec-rowedit-btn" title="Edit"
          onClick={e => { e.stopPropagation(); void openEdit(r.oid); }}>
          <i className="ti ti-pencil" />
        </button>
      ) },
    { field: 'fullName', header: 'Full Name', minWidth: 180, text: r => r.fullName ?? '' },
    { field: 'userName', header: 'User Name', minWidth: 130,
      render: r => inlineCell(r, 'userName', r.userName) },
    // "Lab User" — ALPACA order: between User Name and Role (2026-07-02).
    { field: 'isLabUser', header: 'Lab User', width: 110, align: 'center',
      text: r => r.isLabUser ? 'Yes' : 'No',
      render: r => <span className={`cellcheck${r.isLabUser ? ' on' : ''}`} role="img" aria-label={r.isLabUser ? 'Lab user' : 'Not a lab user'} /> },
    { field: 'role', header: 'Role', minWidth: 160, text: r => r.role ?? '',
      render: r => inlineCell(r, 'role', r.role ?? '') },
    { field: 'department', header: 'Department', minWidth: 140, text: r => r.department ?? '',
      render: r => inlineCell(r, 'department', r.department ?? '') },
    { field: 'isActive', header: 'Is Active', width: 110, align: 'center',
      text: r => r.isActive ? 'Yes' : 'No',
      render: r => <span className={`cellcheck${r.isActive ? ' on' : ''}`} role="img" aria-label={r.isActive ? 'Active' : 'Inactive'} /> },
    { field: 'isLimsAdmin', header: 'Is Admin', width: 110, align: 'center',
      text: r => r.isLimsAdmin ? 'Yes' : 'No',
      render: r => <span className={`cellcheck${r.isLimsAdmin ? ' on' : ''}`} role="img" aria-label={r.isLimsAdmin ? 'Admin' : 'Not admin'} /> },
  ];

  // Full-format "Export to" (all 10 ALPACA formats) of the currently-filtered
  // list — PRESERVES the exact ALPACA column set (not the grid's edit-glyph
  // column) via the shared <ExportMenu> (AppToolbar exportData).
  const exportData: ExportData = {
    filename: 'users',
    headers: USER_EXPORT_COLS.map(c => c.header),
    rows: shown.map(u => USER_EXPORT_COLS.map(c => c.value(u))),
  };

  // ALPACA parity (2026-07-20): legacy shows Copy Roles even on an unsaved
  // NEW record (EmployeeController.cs:238-240 gates purely on the logged-in
  // admin's own permission, not save state) — so the button is visible in
  // 'new' mode too, but since it needs a real target Oid, clicking it before
  // Save explains why instead of opening a picker against a record that
  // doesn't exist yet.
  const copyRolesHandler = (mode === 'view' || mode === 'new') ? () => {
    if (!detail) { notify.warning('Save the user first, then Copy Roles.'); return; }
    setCopyTarget({ oid: detail.oid, name: [form.firstName, form.lastName].filter(Boolean).join(' ') || form.displayName }); setCopyFrom('');
  } : undefined;

  // ── prev / next record arrows walk the CURRENT filtered list ────────────
  const shownIds = useMemo(() => shown.map(u => u.oid), [shown]);
  const idx = selOid ? shownIds.indexOf(selOid) : -1;
  const prevId = idx > 0 ? shownIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < shownIds.length - 1 ? shownIds[idx + 1] : null;

  // ── roles child grid ─────────────────────────────────────────────────────
  const roleCols: AppColumn<Role>[] = [
    { field: 'name', header: 'Role', minWidth: 240 },
    { field: 'isAdministrative', header: 'Administrative', width: 140, align: 'center',
      text: r => r.isAdministrative ? 'Yes' : 'No',
      render: r => <span className={`cellcheck${r.isAdministrative ? ' on' : ''}`} role="img" aria-label={r.isAdministrative ? 'Administrative' : 'Standard'} /> },
  ];

  // ── six ALPACA Employee_DetailView tabs (shared EmployeeDetailForm) ──────
  const tabs: EmployeeTab[] = [
    {
      id: 'basic', label: 'Basic Information', icon: 'ti-user',
      fields: [
        { key: 'firstName', label: 'First Name', value: form.firstName, onChange: v => up('firstName', String(v)), required: true },
        { key: 'lastName', label: 'Last Name', value: form.lastName, onChange: v => up('lastName', String(v)), required: true },
        { key: 'initial', label: 'Initial', value: form.initial, onChange: v => up('initial', String(v)), maxLength: 10 },
        { key: 'jobTitle', label: 'Job Title', value: form.jobTitle, onChange: v => up('jobTitle', String(v)) },
        // ALPACA parity: Employee.DisplayName carries RuleRequiredField + RuleUniqueValue
        // (Employee.cs:1134-1135) — required (uniqueness already enforced server-side,
        // UserAdminController DisplayNameExistsAsync).
        { key: 'displayName', label: 'Display Name (code)', value: form.displayName, onChange: v => up('displayName', String(v)), placeholder: 'e.g. E124690', required: true },
        // ALPACA Employee.Gender enum: 0 = None (default) · 1 = Male · 2 = Female.
        { key: 'gender', label: 'Gender', type: 'radio', value: form.gender ?? 0,
          options: [{ value: '0', label: 'None' }, { value: '1', label: 'Male' }, { value: '2', label: 'Female' }],
          onChange: v => up('gender', Number(v)) },
        { key: 'birthday', label: 'Birthday', type: 'date', value: form.birthday, onChange: v => up('birthday', String(v)) },
      ],
      content: (
        <div className="ud-grid cols-2">
          <div className="ud-field">
            <label>Photo</label>
            <div className="ud-img-box">
              {form.photo ? <img src={form.photo} alt="Employee" /> : <div className="ud-img-empty">No photo</div>}
            </div>
            {mode !== 'view' && (
              <div className="ud-img-actions">
                <label className="ca-btn">Upload<input type="file" accept="image/*" hidden onChange={e => readImgFile(e, v => setForm(p => ({ ...p, photo: v, photoChanged: true })))} /></label>
                {form.photo && <button type="button" className="ca-btn" onClick={() => setForm(p => ({ ...p, photo: null, photoChanged: true }))}>Clear</button>}
              </div>
            )}
          </div>
          <div className="ud-field">
            <label>Signature</label>
            <div className="ud-img-box">
              {form.signature ? <img src={form.signature} alt="Signature" /> : <div className="ud-img-empty">No signature</div>}
            </div>
            {mode !== 'view' && (
              <div className="ud-img-actions">
                <label className="ca-btn">Upload<input type="file" accept="image/*" hidden onChange={e => readImgFile(e, v => setForm(p => ({ ...p, signature: v, signatureChanged: true })))} /></label>
                {form.signature && <button type="button" className="ca-btn" onClick={() => setForm(p => ({ ...p, signature: null, signatureChanged: true }))}>Clear</button>}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'org', label: 'Organization', icon: 'ti-building', cols: 3,
      fields: [
        { key: 'positionId', label: 'Position', type: 'select', value: form.positionId,
          options: lookups.positions.map(p => ({ value: p.id, label: p.name })), onChange: v => up('positionId', String(v)) },
        { key: 'departmentId', label: 'Department', type: 'select', value: form.departmentId,
          options: lookups.departments.map(p => ({ value: p.id, label: p.name })), onChange: v => up('departmentId', String(v)) },
        // ALPACA parity: Employee.Company (captioned "Division") carries
        // [RuleRequiredField] (Employee.cs:118) — required on save.
        { key: 'companyId', label: 'Division', type: 'select', value: form.companyId, required: true,
          options: lookups.companies.map(p => ({ value: p.id, label: p.name })), onChange: v => up('companyId', String(v)) },
        // ALPACA field parity (2026-07-02): the Employee employment window.
        { key: 'employmentDate', label: 'Employment Date', type: 'date', value: form.employmentDate, onChange: v => up('employmentDate', String(v)) },
        { key: 'retireDate', label: 'Retire Date', type: 'date', value: form.retireDate, onChange: v => up('retireDate', String(v)) },
      ],
    },
    {
      id: 'contact', label: 'Contact', icon: 'ti-address-book',
      // ALPACA field parity (2026-07-02): the full Employee contact set —
      // phones / fax + the Country → State → City cascading lookups + Zip /
      // Address.  Picking a country narrows states; picking a state narrows
      // cities (a change clears the now-invalid children, legacy behaviour).
      fields: [
        // ALPACA parity: PropertyEditor Id="Email" Caption="Delivery Email"
        // (Model.xafml:22026), RuleRequiredField (Model.xafml:2383).
        { key: 'email', label: 'Delivery Email', type: 'email', value: form.email, onChange: v => up('email', String(v)), required: true },
        { key: 'tFAEmail', label: '2FA Email', type: 'email', value: form.tFAEmail, onChange: v => up('tFAEmail', String(v)) },
        { key: 'mobilePhone', label: 'Mobile Phone', value: form.mobilePhone, onChange: v => up('mobilePhone', String(v)) },
        { key: 'officePhone', label: 'Office Phone', value: form.officePhone, onChange: v => up('officePhone', String(v)) },
        { key: 'homePhone', label: 'Home Phone', value: form.homePhone, onChange: v => up('homePhone', String(v)) },
        { key: 'otherPhone', label: 'Other Phone', value: form.otherPhone, onChange: v => up('otherPhone', String(v)) },
        { key: 'fax', label: 'Fax', value: form.fax, onChange: v => up('fax', String(v)) },
        { key: 'countryId', label: 'Country', type: 'select', value: form.countryId,
          options: lookups.countries.map(c => ({ value: c.id, label: c.name })),
          onChange: v => setForm(p => ({ ...p, countryId: String(v), stateId: '', cityId: '' })) },
        { key: 'stateId', label: 'State', type: 'select', value: form.stateId,
          options: lookups.states.filter(s => !form.countryId || s.parentId === form.countryId).map(s => ({ value: s.id, label: s.name })),
          onChange: v => setForm(p => ({ ...p, stateId: String(v), cityId: '' })) },
        { key: 'cityId', label: 'City', type: 'select', value: form.cityId,
          options: lookups.cities.filter(c => !form.stateId || c.parentId === form.stateId).map(c => ({ value: c.id, label: c.name })),
          onChange: v => up('cityId', String(v)) },
        { key: 'zip', label: 'Zip', value: form.zip, onChange: v => up('zip', String(v)) },
        { key: 'address', label: 'Address', type: 'textarea', value: form.address, onChange: v => up('address', String(v)) },
      ],
    },
    {
      // ALPACA parity: legacy's Resume tab is a full rich-text (HTML) editor
      // (Model.xafml Item4/Resume, an HtmlPropertyEditor with Design/HTML/
      // Preview modes) — not a plain textarea. Reuses the SAME <RichTextEditor>
      // already built for this exact purpose (Settings → System Management →
      // Employees), never a second implementation (standing rule 8).
      id: 'resume', label: 'Resume', icon: 'ti-file-text',
      content: mode === 'view'
        ? <div className="ud-ro" style={{ minHeight: 90 }} dangerouslySetInnerHTML={{ __html: form.resume || '—' }} />
        : <RichTextEditor value={form.resume} onChange={v => up('resume', v)} />,
    },
    {
      // ALPACA parity (2026-07-20): field ORDER now matches legacy's single-column
      // User Information layout exactly (Model.xafml Item1, Auto21586c09 group,
      // Index 0-9): Email → Password → Is Lab User → Is LIMS Administrator →
      // Is Active → Is Enabled Language → Language. The password slot is legacy's
      // SetPassword+"Set Password" button (Model.xafml:22030-22031) on an existing
      // record — AI's equivalent for an unsaved NEW one is a real inline password
      // field (a checkbox+button has nothing to open yet), placed at the SAME
      // sequence position rather than shoved below everything else.
      id: 'user', label: 'User Information', icon: 'ti-shield-lock',
      fields: [
        // ALPACA parity: PropertyEditor Id="UserName" Caption="Email" (Model.xafml:22033)
        // — this IS the login identifier; the LABEL is legacy's own convention (this
        // lab formats usernames as email addresses). Distinct from Contact's
        // "Delivery Email" (Employee.Email) and "2FA Email" (TFAEmail) — legacy shows
        // all three, differently captioned, on the same form.
        { key: 'userName', label: 'Email', value: form.userName, onChange: v => up('userName', String(v)), required: true },
        // ALPACA parity: PropertyEditor Id="SetPassword" Caption="Password", AllowEdit="False"
        // (Model.xafml:22030) — a READ-ONLY indicator paired with the "setpasswordButton"
        // action (Model.xafml:22031), on BOTH new and existing records (legacy's New form
        // shows this too — unchecked until a password is provided via the SAME dialog).
        // The checkbox itself is non-interactive (no onChange — legacy doesn't let you
        // toggle it directly either); "Set Password" is the only way to change it, whether
        // the record is saved yet or not (setPassword() branches on pwdTarget.isNew).
        { key: 'hasPassword', label: 'Password', type: 'checkbox' as const,
          value: mode === 'new' ? !!newPwd : (detail?.hasPassword ?? false),
          required: mode === 'new',
          action: {
            label: 'Set Password', icon: 'ti-key',
            onClick: () => {
              if (mode === 'new') { setPwdTarget({ oid: '', userName: form.userName || 'New User', isNew: true }); }
              else if (detail) { setPwdTarget({ oid: detail.oid, userName: detail.userName }); }
              setPwdValue(''); setPwdConfirm('');
            },
          } },
        { key: 'isLabUser', label: 'Is Lab User', type: 'checkbox', value: form.isLabUser, onChange: v => up('isLabUser', !!v) },
        { key: 'isLimsAdmin', label: 'LIMS Administrator (full access — bypasses screen gating)', type: 'checkbox', value: form.isLimsAdmin, onChange: v => up('isLimsAdmin', !!v) },
        ...(mode !== 'new' && detail
          ? [{ key: 'isActive', label: 'Is Active', type: 'checkbox' as const, value: detail.isActive }]
          : []),
        { key: 'isEnabledLanguage', label: 'Is Enabled Language', type: 'checkbox', value: form.isEnabledLanguage, onChange: v => up('isEnabledLanguage', !!v) },
        // ALPACA Employee.Language int enum: 0 = Chinese · 1 = English.
        { key: 'language', label: 'Language', type: 'radio', value: form.language,
          options: [{ value: '0', label: 'Chinese' }, { value: '1', label: 'English' }],
          onChange: v => up('language', Number(v)) },
        // ALPACA field parity (2026-07-02): PermissionPolicyUser.ChangePasswordOnFirstLogon
        // — an AI/legacy field with no fixed legacy tab slot on THIS layout; kept
        // adjacent to the password fields it's semantically related to.
        { key: 'changePasswordOnFirstLogon', label: 'Change Password On First Logon', type: 'checkbox',
          value: form.changePasswordOnFirstLogon, onChange: v => up('changePasswordOnFirstLogon', !!v) },
      ],
      content: mode === 'edit' && detail ? (
        <div className="ud-sec-toolbar">
          <button className="ca-btn" onClick={toggleActive}>
            <i className={detail.isActive ? 'ti ti-user-off' : 'ti ti-user-check'} /> {detail.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <span className="ud-sec-hint">Account #{detail.userId || '—'} · activate/deactivate applies immediately.</span>
        </div>
      ) : undefined,
    },
    ...(SHOW_ROLES_TAB ? [{
      id: 'roles', label: `Roles (${userRoles.length})`, icon: 'ti-users-group',
      content: mode === 'new' ? (
        <div className="ud-hint">Save the user first — roles can be linked once the account exists.</div>
      ) : (
        <>
          {/* Child-grid section toolbar: Link · Unlink (persist via the existing
              PUT roles call).  Hidden in read-only VIEW mode (Edit unlocks). */}
          {mode !== 'view' && (
            <div className="ud-sec-toolbar">
              <button className="ca-btn ca-btn-solid" onClick={() => setLinkOpen(true)} disabled={savingRoles}>
                <i className="ti ti-link" /> Link
              </button>
              <button className="ca-btn" onClick={unlinkSelected} disabled={savingRoles || roleSel.size === 0}>
                {savingRoles ? <BusyIcon busy icon="ti-unlink" /> : <i className="ti ti-unlink" />} Unlink
              </button>
              <span className="ud-sec-hint">Roles gate which screens the user sees. Changes take effect within ~1 minute (permission cache).</span>
            </div>
          )}
          <AppDataGrid<Role>
            rows={assignedRoles}
            columns={roleCols}
            getRowId={r => r.id}
            selectable
            selectedIds={roleSel}
            onSelectionChange={setRoleSel}
            emptyText="No roles linked to this user."
          />
        </>
      ),
    }] : []),
  ];

  const detailName = mode === 'new'
    ? 'New User'
    : [form.firstName, form.lastName].filter(Boolean).join(' ') || form.displayName || form.userName || '…';

  // ═════════════════════════════ DETAIL VIEW ═══════════════════════════════
  if (view === 'detail') {
    return (
      <div className="ud-page">
        {/* List/Detail segmented switch — Detail active; List returns to the grid. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') backToList(); }} />
        </div>
        <div className="ud-detail">
          {/* Header — "Users – {record}" + breadcrumb + prev/next record arrows */}
          <div className="ud-dhead">
            <div>
              <h2>
                Users – {detailName}
                {mode !== 'new' && selOid && (
                  <AuditTrailButton variant="chip" formName="Users" sourceId={selOid} />
                )}
              </h2>
              <div className="ud-bc">
                <a onClick={backToList}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>User &amp; Security</span>
                <i className="ti ti-chevron-right" />
                <a onClick={backToList}>Users</a>
                <i className="ti ti-chevron-right" />
                <span>{detailName}</span>
              </div>
            </div>
            <div className="ud-navarrows">
              <button className="ud-na" title="Previous record" disabled={!prevId || saving}
                onClick={() => prevId && openEdit(prevId)}>‹</button>
              <button className="ud-na next" title="Next record" disabled={!nextId || saving}
                onClick={() => nextId && openEdit(nextId)}>›</button>
            </div>
          </div>

          {/* Action bar — TOP (identical set at the bottom).  VIEW mode shows
              Edit + Close (ALPACA parity); Edit switches this SAME detail to
              edit mode. */}
          <ActionBar viewOnly={mode === 'view'} onEdit={() => setMode('edit')}
            onSave={() => save('stay')} onSaveAndClose={() => save('close')} onSaveAndNew={() => save('new')}
            onCancel={backToList} saving={saving}
            onNew={openNew} onDelete={mode === 'view' ? deleteCurrent : undefined} onRefresh={refreshCurrent}
            onSetPassword={mode === 'view' && detail ? () => { setPwdTarget({ oid: detail.oid, userName: detail.userName }); setPwdValue(''); } : undefined}
            onCopyRoles={copyRolesHandler} />

          <EmployeeDetailForm tabs={tabs} active={dtab} readOnly={mode === 'view'}
            onTabChange={id => setDtab(id as TabId)} />

          {/* Action bar — BOTTOM */}
          <ActionBar bottom viewOnly={mode === 'view'} onEdit={() => setMode('edit')}
            onSave={() => save('stay')} onSaveAndClose={() => save('close')} onSaveAndNew={() => save('new')}
            onCancel={backToList} saving={saving}
            onNew={openNew} onDelete={mode === 'view' ? deleteCurrent : undefined} onRefresh={refreshCurrent}
            onSetPassword={mode === 'view' && detail ? () => { setPwdTarget({ oid: detail.oid, userName: detail.userName }); setPwdValue(''); } : undefined}
            onCopyRoles={copyRolesHandler} />
        </div>

        {roleLinkDialog()}

        {/* ── Set Password / Copy Roles dialogs (ALPACA parity — live on the open
            record's own detail, not just the list toolbar) — same shared dialogs
            the list view uses, just also reachable from here. ── */}
        <AppDialog open={!!pwdTarget} title={`Set Password — ${pwdTarget?.userName ?? ''}`} icon="ti-key" width={400}
          onClose={() => { setPwdTarget(null); setPwdConfirm(''); }}
          footer={<>
            <button className="ca-btn" onClick={() => { setPwdTarget(null); setPwdConfirm(''); }}><span className="tb-i">↩︎</span> Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={setPassword}><span className="tb-i">💾</span> Set Password</button>
          </>}>
          <div className="ud-dlg-body">
            {/* ALPACA parity: legacy's Set Password dialog is New + Confirm Password
                (screenshot, same shape whether the record is new or existing). */}
            <div className="ud-field">
              <label>New Password <span className="ud-req">*</span></label>
              <input className="ud-input" type="password" value={pwdValue} onChange={e => setPwdValue(e.target.value)} autoFocus />
            </div>
            <div className="ud-field">
              <label>Confirm Password <span className="ud-req">*</span></label>
              <input className="ud-input" type="password" value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)} />
            </div>
            <span className="ud-hint">Stored in XAF's salted SHA-512 — works for login in both Alpaca Intelligence and ALPACA.</span>
          </div>
        </AppDialog>

        <AppDialog open={!!copyTarget} title={`Copy Roles — onto ${copyTarget?.name ?? ''}`} icon="ti-copy" width={440}
          onClose={() => setCopyTarget(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setCopyTarget(null)}><span className="tb-i">↩︎</span> Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={copyRoles} disabled={!copyFrom}><i className="ti ti-copy" /> Copy &amp; Apply</button>
          </>}>
          <div className="ud-dlg-body">
            <span className="ud-hint">Pick a user — their roles replace the selected user's current roles.</span>
            <div className="ud-field">
              <label>Copy roles from</label>
              <ComboSelect value={copyFrom} placeholder="— select a user —"
                options={list.filter(u => u.oid !== copyTarget?.oid).map(u => ({ value: u.oid, label: `${u.fullName || u.displayName} (${u.userName})` }))}
                onChange={setCopyFrom} />
            </div>
          </div>
        </AppDialog>
      </div>
    );
  }

  // ═════════════════════════════ LIST VIEW (landing) ═══════════════════════
  return (
    <div className="ud-page">
      {/* List/Detail segmented switch (BO UI Standard) — top-LEFT, same
          placement as ModuleListView. Detail opens the checkbox-selected
          row (exactly one) or the first row of the current list. */}
      <div style={{ padding: '10px 0 0' }}>
        <ViewToggle view="list" detailDisabled={shown.length === 0}
          onChange={(v) => {
            if (v !== 'detail') return;
            const id = selected.size === 1 ? String(Array.from(selected)[0]) : shown[0]?.oid;
            if (id) void openEdit(id);
          }} />
      </div>
      <FormTitle pageId="sec-users" loadMs={loadMs} loading={loading} auditFormName="Users" />
      <div className="ud-sub">{shown.length} item{shown.length === 1 ? '' : 's'}</div>

      {/* Shared toolbar — canonical order; form-specific: Set Password · Copy Roles. */}
      <div className="ud-toolbar-row">
        <AppToolbar
          left={<>
            <button className="ca-btn" disabled={!selectedRow}
              onClick={() => { if (selectedRow) { setPwdTarget({ oid: selectedRow.oid, userName: selectedRow.userName }); setPwdValue(''); } }}
              title="Set Password">
              <i className="ti ti-key" /> Set Password
            </button>
            <button className="ca-btn" disabled={!selectedRow}
              onClick={() => { if (selectedRow) { setCopyTarget({ oid: selectedRow.oid, name: selectedRow.fullName || selectedRow.displayName }); setCopyFrom(''); } }}
              title="Copy Roles">
              <i className="ti ti-copy" /> Copy Roles
            </button>
            {/* "Default E-mail Recipient" (ALPACA) — intentionally NOT here: the legacy
                action edits the dbo.DefaultMailRecipient singleton, which AI already
                owns via Settings → Report Delivery Settings (standing rule 8). */}
          </>}
          onNew={openNew}
          onEdit={() => { if (selectedRow) void openEdit(selectedRow.oid); }}
          editDisabled={!selectedRow}
          onDelete={() => { if (selectedRow) void del(selectedRow); }}
          deleteDisabled={!selectedRow}
          onRefresh={load}
          refreshing={loading}
          exportData={exportData}
        />
      </div>

      {/* Shared search row + the show-inactive filter */}
      <div className="list-search-row">
        <div className="list-search">
          <span className="tb-i">🔍</span>
          <input placeholder="Search users…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
        </div>
        <label className="ud-check" style={{ padding: 0 }}>
          <input type="checkbox" checked={showInactive} onChange={e => { setShowInactive(e.target.checked); setSelected(new Set()); }} />
          Show inactive users only
        </label>
      </div>

      {/* ONE white card: the shared grid (footer/pager/page-size live inside it). */}
      <div className="ud-card">
        {err ? (
          <div className="ud-err"><i className="ti ti-alert-circle" /> {err}</div>
        ) : (
          <AppDataGrid<ListItem>
            rows={shown}
            columns={cols}
            getRowId={r => r.oid}
            loading={loading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onRowClick={r => void openEdit(r.oid)}
            emptyText="No users found."
          />
        )}
      </div>

      {/* §9 bulk-copy menu (2026-07-02) — Department / Role → the bulk endpoint
          (ONE request, ONE audit row).  All = the current filtered list;
          Selected = the checkbox selection. */}
      {bulkCtx && (
        <CellContextMenu
          value={bulkCtx.key === 'role' ? bulkCtx.row.role : bulkCtx.row.department}
          columnLabel={bulkCtx.key === 'role' ? 'Role' : 'Department'}
          selectedCount={selected.size}
          position={{ x: bulkCtx.x, y: bulkCtx.y }}
          onCopyAll={() => void bulkApply('all')}
          onCopySelected={() => void bulkApply('selected')}
          onClose={() => setBulkCtx(null)}
        />
      )}

      {/* ── Set Password dialog (shared AppDialog) ── */}
      <AppDialog open={!!pwdTarget} title={`Set Password — ${pwdTarget?.userName ?? ''}`} icon="ti-key" width={400}
        onClose={() => { setPwdTarget(null); setPwdConfirm(''); }}
        footer={<>
          <button className="ca-btn" onClick={() => { setPwdTarget(null); setPwdConfirm(''); }}><span className="tb-i">↩︎</span> Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={setPassword}><span className="tb-i">💾</span> Set Password</button>
        </>}>
        <div className="ud-dlg-body">
          <div className="ud-field">
            <label>New Password <span className="ud-req">*</span></label>
            <input className="ud-input" type="password" value={pwdValue} onChange={e => setPwdValue(e.target.value)} autoFocus />
          </div>
          <div className="ud-field">
            <label>Confirm Password <span className="ud-req">*</span></label>
            <input className="ud-input" type="password" value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)} />
          </div>
          <span className="ud-hint">Stored in XAF's salted SHA-512 — works for login in both Alpaca Intelligence and ALPACA.</span>
        </div>
      </AppDialog>

      {/* ── Copy Roles dialog (shared AppDialog + ComboSelect) ── */}
      <AppDialog open={!!copyTarget} title={`Copy Roles — onto ${copyTarget?.name ?? ''}`} icon="ti-copy" width={440}
        onClose={() => setCopyTarget(null)}
        footer={<>
          <button className="ca-btn" onClick={() => setCopyTarget(null)}><span className="tb-i">↩︎</span> Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={copyRoles} disabled={!copyFrom}><i className="ti ti-copy" /> Copy &amp; Apply</button>
        </>}>
        <div className="ud-dlg-body">
          <span className="ud-hint">Pick a user — their roles replace the selected user's current roles.</span>
          <div className="ud-field">
            <label>Copy roles from</label>
            <ComboSelect value={copyFrom} placeholder="— select a user —"
              options={list.filter(u => u.oid !== copyTarget?.oid).map(u => ({ value: u.oid, label: `${u.fullName || u.displayName} (${u.userName})` }))}
              onChange={setCopyFrom} />
          </div>
        </div>
      </AppDialog>
    </div>
  );

  // ── Roles "Link" picker (shared DualListPickerDialog → existing PUT roles) ──
  function roleLinkDialog() {
    return (
      <DualListPickerDialog
        open={linkOpen}
        title={`Link Roles — ${detailName}`}
        icon="ti-link"
        available={allRoles.map(r => ({ id: r.id, label: r.name, hint: r.isAdministrative ? 'Admin' : undefined }))}
        selectedIds={assignedRoles.map(r => r.id)}
        availableTitle="Unassigned roles"
        selectedTitle="Linked roles"
        onCancel={() => setLinkOpen(false)}
        onOk={ids => { setLinkOpen(false); applyRoles(ids, 'Roles updated successfully.'); }}
      />
    );
  }
}

/** Detail action bar — rendered at TOP and BOTTOM.  Edit mode (ALPACA XAF
 *  parity, 2026-07-21): Save · Save and Close · Save and New · Cancel, in
 *  that exact order, right-aligned.  Read-only VIEW mode (ALPACA parity):
 *  New · Edit · Delete · Refresh · Close (canonical CLAUDE.md §18.6 order —
 *  Audit Trail lives on the title row, not here, per BO UI Standard v2
 *  ruling 2026-07-01). */
function ActionBar({ bottom, viewOnly, onEdit, onSave, onSaveAndClose, onSaveAndNew, onCancel, saving, onNew, onDelete, onRefresh, onSetPassword, onCopyRoles }: {
  bottom?: boolean; viewOnly?: boolean; onEdit: () => void;
  onSave: () => void; onSaveAndClose: () => void; onSaveAndNew: () => void; onCancel: () => void; saving: boolean;
  onNew?: () => void; onDelete?: () => void; onRefresh?: () => void;
  /** ALPACA parity — the "Set Password" / "Copy Roles" actions live on the OPEN
   *  Employee detail record itself (EmployeeController), not just the list
   *  toolbar (which still has its own copies for a checkbox-selected row). */
  onSetPassword?: () => void; onCopyRoles?: () => void;
}) {
  return (
    <div className={`ud-actionbar${bottom ? ' bottom' : ''}`}>
      {viewOnly ? (
        <>
          {onNew && (
            <button className="ca-btn" onClick={onNew}>
              <span className="tb-i tb-i-green">＋</span> New
            </button>
          )}
          <button className="ca-btn" onClick={onEdit}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          {onDelete && (
            <button className="ca-btn" onClick={onDelete}>
              <span className="tb-i tb-i-red">🗑</span> Delete
            </button>
          )}
          {onRefresh && (
            <button className="ca-btn" onClick={onRefresh}>
              <span className="tb-i tb-i-green">🔄</span> Refresh
            </button>
          )}
          {onSetPassword && (
            <button className="ca-btn" onClick={onSetPassword}>
              <i className="ti ti-key" /> Set Password
            </button>
          )}
          {onCopyRoles && (
            <button className="ca-btn" onClick={onCopyRoles}>
              <i className="ti ti-copy" /> Copy Roles
            </button>
          )}
          <button className="ca-btn" onClick={onCancel}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      ) : (
        <>
          <button className="ca-btn ca-btn-solid" onClick={onSave} disabled={saving}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
          <button className="ca-btn" onClick={onSaveAndClose} disabled={saving}>
            <span className="tb-i">💾</span> Save and Close
          </button>
          <button className="ca-btn" onClick={onSaveAndNew} disabled={saving}>
            <i className="ti ti-file-plus" /> Save and New
          </button>
          <button className="ca-btn" onClick={onCancel} disabled={saving}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
          {/* ALPACA parity (2026-07-20): Copy Roles visible even before Save
              (EmployeeController.cs:238-240 — gated on the admin's own
              permission, not save state). Only truthy here in 'new' mode —
              the caller's copyRolesHandler is undefined during 'edit'. */}
          {onCopyRoles && (
            <button className="ca-btn" onClick={onCopyRoles}>
              <i className="ti ti-copy" /> Copy Roles
            </button>
          )}
        </>
      )}
    </div>
  );
}
